import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info, Star, Tree } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/shared";
import { Gauge, ScoreBar } from "@/components/frameworkBits";
import { FrameworkSwitcher } from "@/components/FrameworkSwitcher";
import { fmtPeriod, NOT_ASSESSED } from "@/lib/period";
import { toast } from "sonner";

// Panchatanthra's five elements share one ember ramp so the framework reads as a whole
const TINTS = [
  { text: "text-[hsl(20_86%_40%)]", bar: "bg-[hsl(20_86%_45%)]", soft: "bg-[hsl(20_86%_45%)]/10" },
  { text: "text-[hsl(14_72%_46%)]", bar: "bg-[hsl(14_74%_54%)]", soft: "bg-[hsl(14_74%_54%)]/10" },
  { text: "text-[hsl(30_88%_38%)]", bar: "bg-[hsl(30_90%_48%)]", soft: "bg-[hsl(30_90%_48%)]/10" },
  { text: "text-goldink", bar: "bg-gold", soft: "bg-gold/12" },
  { text: "text-[hsl(38_88%_34%)]", bar: "bg-[hsl(38_92%_44%)]", soft: "bg-[hsl(38_92%_44%)]/10" },
];

export default function PanchatanthraOverview() {
  const { tenantId } = useAuth();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    api
      .get(`/tenants/${tenantId}/panchatanthra/overview`, { params: period ? { period } : {} })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [tenantId, period]);

  if (!data) return <p className="label-caps animate-pulse" data-testid="pt-overview-loading">Loading overview</p>;

  return (
    <div className="space-y-10" data-testid="pt-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FrameworkSwitcher current="panchatanthra" />
        {(data.periods || []).length > 0 && (
          <select
            data-testid="pt-period-select"
            value={period || data.selected_period || ""}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-10 rounded-full border border-border bg-card px-5 text-xs text-navy transition-colors hover:border-gold"
          >
            {data.periods.map((p) => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
          </select>
        )}
      </div>
      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="grid grid-cols-1 gap-8 p-6 md:p-8 lg:grid-cols-[1.15fr_1fr_0.6fr] lg:gap-10">
          <div className="flex items-start gap-5">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-navy text-white">
              <Tree size={30} />
            </div>
            <div>
              <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">Panchatanthra</h1>
              <p className="mt-1 text-base font-medium text-deepblue">Run your business strategically.</p>
              <div className="brand-bar mt-3 h-[3px] w-28 rounded-full" />
              <p className="mt-4 max-w-md text-sm text-foreground/80">
                Panchatanthra helps you build a valuable, sustainable and future-ready business. It evaluates five
                critical elements that drive long-term value creation.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-start gap-4 border-border lg:border-l lg:pl-10">
            <p className="label-caps">Overall business score</p>
            <div className="flex items-center gap-6">
              <Gauge value={data.overall_score10} testid="pt-overall-gauge" />
              <div>
                <p className="font-mono text-5xl font-semibold tabular-nums text-navy" data-testid="pt-overall-score">
                  {data.overall_score10 ?? "—"}
                  <span className="text-xl font-semibold text-[hsl(230_28%_30%)]"> /10</span>
                </p>
                <p className="mt-1 text-sm font-medium text-deepblue">Combined score across 5 business elements</p>
              </div>
            </div>
            <p className="flex items-start gap-2 rounded-md bg-sky/15 px-3 py-2 text-xs text-navy">
              <Info size={15} className="mt-0.5 shrink-0" />
              {data.indicative
                ? "Indicative view — no published score snapshot yet for this engagement."
                : `Score reflects all 5 business elements combined · ${data.cycle_name} · ${fmtPeriod(data.period_label) || "—"}`}
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 border-border lg:items-center lg:border-l lg:pl-8">
            <Star size={22} className="text-gold" weight="fill" />
            <p className="label-caps lg:text-center">Performance level</p>
            <p className="font-display text-2xl text-navy lg:text-center" data-testid="pt-performance-level">
              {data.performance_level}
            </p>
            <p className="card-body lg:text-center">
              Version {data.version_label || "—"} · {data.version_state || "draft"}
            </p>
          </div>
        </div>
      </div>

      <section>
        <div className="mb-5 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="label-caps">5 business elements</p>
            <p className="mt-2 card-body">Weighted contributors to the overall business score. Open a drill-down for the survey, summaries, commitments and weekly review.</p>
          </div>
        </div>

        {data.elements.length === 0 ? (
          <EmptyState
            testid="pt-elements-empty"
            title="No questionnaire version"
            message="Publish a questionnaire version (or import a workbook) to see the five business elements."
          />
        ) : (
          <div className="stagger grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 md:gap-5">
            {data.elements.map((el, i) => {
              const t = TINTS[i % TINTS.length];
              return (
                <div
                  key={el.code}
                  data-testid={`pt-element-card-${el.code}`}
                  className="panel panel-hover flex h-full flex-col overflow-hidden"
                >
                  <div className={`h-1 w-full ${t.bar}`} />
                  <div className="flex flex-1 flex-col p-5">
                    <div className={`mb-3 grid h-9 w-9 place-items-center rounded-lg ${t.soft} ${t.text}`}>
                      <span className="font-mono text-sm">{i + 1}</span>
                    </div>
                    <h3 className={`text-sm font-semibold uppercase leading-snug tracking-[0.06em] ${t.text}`}>
                      {el.name}
                    </h3>
                    <p className="mt-4 font-mono text-4xl font-semibold tabular-nums text-navy">
                      {el.score10 ?? "—"}
                      <span className="text-base font-semibold text-[hsl(230_28%_30%)]"> /10</span>
                    </p>
                    <ScoreBar value={el.score10} className={t.bar} />
                    {el.score10 == null && (
                      <p className="mt-1 text-xs font-bold uppercase tracking-[0.1em] text-[hsl(230_30%_28%)]"
                         data-testid={`pt-element-unassessed-${el.code}`}>{NOT_ASSESSED}</p>
                    )}
                    <p className="label-caps mt-3">Weightage: {el.weight_pct ?? "—"}%</p>
                    <ul className="mt-4 flex-1 space-y-1.5 border-t border-border pt-4">
                      {el.sub_elements.map((s) => (
                        <li key={s} className="flex gap-2 text-sm font-medium text-[hsl(230_28%_28%)]">
                          <span className={`mt-1.5 h-1 w-1 shrink-0 rounded-full ${t.bar}`} />
                          {s}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/frameworks/panchatanthra/elements/${el.code}`}
                      data-testid={`pt-element-open-${el.code}`}
                      className={`mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] ${t.text}`}
                    >
                      Drill down <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
